import { createFilterOptions } from "@mui/material";
import { isEqual, isNil } from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  useBulkEditFormContext,
  useCrudEditContext,
  useDropdownSourceContext,
} from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  companyIdSelector,
  CrudApi,
  doesBulkActionKeepAssignedInactiveOptions,
  EditFormMode,
  EntitiesWithoutPreCreateForm,
  errors as errorsConst,
  errorsCrudContentAtomFamily,
  getBulkAncestorAllowedValues,
  getBulkFormFieldName,
  getBulkMultiselectAssignedValuesUnion,
  isAddDropdownButtonVisibleSelector,
  isSimpleDto,
  loadingEditFormContentAtomFamily,
  Log,
  removeMetadataPrefix,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultFalse,
  simpleResultTrue,
  sortArrayAlphabetically,
  unique,
  useDrawers,
  useHasPermissions,
  ValidationHelpers,
  type IDropdownItem,
  type IDropdownItemExt,
  type IExtendableDto,
  type IFormAttributes,
  type IValidationsYupRoot,
  type RydeUiUnit,
  type TDropdownMode,
} from "@ryde/shared";

import { useDropdownCustomTransformation } from "./custom-transformers";
import {
  dropDownBoundTemplateFormat,
  getDropdownOptions,
} from "./dropdown-bound.utils";
import { useFindAreaButton } from "./use-find-area-button";

export const useDropdownTemplate = (
  multiMode: TDropdownMode,
  name: string,
  formAttributes: IFormAttributes | null | undefined,
  formValidation: IValidationsYupRoot | null | undefined,
) => {
  const { t } = useTranslation();
  const companyId = useRecoilValue(companyIdSelector);
  const { entity } = useCrudEditContext<IExtendableDto>();
  const { isBulkEditForm, selectedRowsValues } = useBulkEditFormContext();
  const { getTransformedDropdownItems } = useDropdownCustomTransformation();
  const setErrors = useSetRecoilState(errorsCrudContentAtomFamily(entity));
  const setLoading = useSetRecoilState(
    loadingEditFormContentAtomFamily(entity),
  );
  const [isLoadingItems, startTransition] = useTransition();

  const {
    getValues,
    setValue,
    resetField,
    watch,
    formState: { errors },
  } = useFormContext();

  const {
    dropItemsSource,
    dropItemsDisableAdd,
    dropdownItemsFilter,
    excludeIds,
    disabled,
    disableSorting,
    customDropdownItemsFilter,
    dropdownDataTransformationFn,
    dropDownTemplate,
    displayTextReplacedByFieldName,
    clearedByAncestorMessageKey,
    dropdownItemsSuffixOverride,
    dropdownItemsResolverName,
    dropdownItemsReloadKey,
  } = formAttributes || {};

  const { hasPermissions: isHasCreatePermission } = useHasPermissions({
    entity: dropItemsSource ?? "unknown",
    action: "create",
  });

  const { openNewOrDuplicateDrawer, openEditFormDrawer } = useDrawers();
  const isAddDropdownButtonVisible = useRecoilValue(
    isAddDropdownButtonVisibleSelector,
  );

  const { getDropdownItems, getDropdownLabels } = useDropdownSourceContext();
  const {
    ancestorFieldName,
    ownFieldName,
    customActiveFieldName,
    filters,
    isIgnoreFilterUntilAncestorIsNotChosen,
    ancestorItemsSource,
    matchAncestorItemArrayKey,
  } = dropdownItemsFilter || {};
  const sourceFieldValue = ancestorFieldName
    ? watch(ancestorFieldName)
    : undefined;

  // When filtering by an array on the selected ancestor item (e.g. a shift's mealIds),
  // load the ancestor source items so we can read that array for the chosen ancestor value.
  const [ancestorItems, setAncestorItems] = useState<
    IDropdownItem[] | undefined
  >(undefined);

  useEffect(() => {
    if (!matchAncestorItemArrayKey || !ancestorItemsSource) {
      return;
    }

    let isCancelled = false;

    const loadAncestorItems = async () => {
      const items = await getDropdownItems?.(ancestorItemsSource);

      if (!isCancelled) {
        setAncestorItems(items ?? []);
      }
    };

    void loadAncestorItems();

    return () => {
      isCancelled = true;
    };
  }, [ancestorItemsSource, matchAncestorItemArrayKey, getDropdownItems]);

  // The bulk form has no ancestor field to watch — the ancestor (e.g. each booking's shift) lives on the
  // selected rows, so the allowed options are what all of them have in common. Undefined outside bulk.
  const bulkAllowedValues = useMemo<(string | number)[] | undefined>(() => {
    if (!isBulkEditForm || !ancestorFieldName || !matchAncestorItemArrayKey) {
      return undefined;
    }

    return getBulkAncestorAllowedValues({
      selectedRowsValues,
      ancestorItems,
      ancestorFieldName,
      matchAncestorItemArrayKey,
    });
  }, [
    ancestorFieldName,
    ancestorItems,
    isBulkEditForm,
    matchAncestorItemArrayKey,
    selectedRowsValues,
  ]);

  const ancestorAllowedValues = useMemo<(string | number)[] | undefined>(() => {
    if (
      !matchAncestorItemArrayKey ||
      isNil(sourceFieldValue) ||
      !ancestorItems
    ) {
      return undefined;
    }

    const ancestorItem = ancestorItems.find(
      item => String(item.value) === String(sourceFieldValue),
    );
    const arrayValue = ancestorItem
      ? (ancestorItem as IDropdownItem & Record<string, unknown>)[
          matchAncestorItemArrayKey
        ]
      : undefined;

    return Array.isArray(arrayValue) ? (arrayValue as (string | number)[]) : [];
  }, [matchAncestorItemArrayKey, sourceFieldValue, ancestorItems]);

  const allowedValues = bulkAllowedValues ?? ancestorAllowedValues;

  const bulkAction = isBulkEditForm
    ? watch(getBulkFormFieldName(name))
    : undefined;
  // ADD/REPLACE must not list inactive/orphan shifts. REMOVE keeps them so a
  // deselected chip can still be picked again, including ids missing from the catalog.
  const keepAssignedInactiveOptions =
    doesBulkActionKeepAssignedInactiveOptions(bulkAction);

  const bulkAssignedValuesUnion = useMemo(() => {
    if (
      !isBulkEditForm ||
      multiMode === "single" ||
      !keepAssignedInactiveOptions
    ) {
      return [];
    }

    return getBulkMultiselectAssignedValuesUnion({
      selectedRowsValues,
      fieldName: name,
    });
  }, [
    isBulkEditForm,
    keepAssignedInactiveOptions,
    multiMode,
    name,
    selectedRowsValues,
  ]);

  const bulkAssignedValuesSet = useMemo(
    () => new Set(bulkAssignedValuesUnion.map(String)),
    [bulkAssignedValuesUnion],
  );

  const currentFieldValue = getValues(name);
  const displayTextReplaced = displayTextReplacedByFieldName
    ? getValues(displayTextReplacedByFieldName)
    : undefined;

  const [dropdownItems, setDropdownItems] = useState<
    IDropdownItem[] | undefined
  >(undefined);
  const [inputText, setInputText] = useState("");

  const requiredComputed = useMemo(
    () => ValidationHelpers.hasRequiredRule(formValidation),
    [formValidation],
  );

  const exclusiveIds = useMemo(() => {
    return (
      dropdownItems?.flatMap(({ isExclusive, value }) =>
        isExclusive ? [Number(value)] : [],
      ) ?? []
    );
  }, [dropdownItems]);

  const isDisabledByMissingAncestor = useMemo(() => {
    // Bulk resolves the ancestor from the selected rows, so there is no ancestor field left to fill in.
    if (bulkAllowedValues) {
      return false;
    }

    if (isIgnoreFilterUntilAncestorIsNotChosen !== undefined) {
      return (
        !isIgnoreFilterUntilAncestorIsNotChosen &&
        !!dropdownItemsFilter?.ancestorFieldName &&
        !sourceFieldValue
      );
    }

    return !!dropdownItemsFilter?.ancestorFieldName && !sourceFieldValue;
  }, [
    bulkAllowedValues,
    dropdownItemsFilter,
    isIgnoreFilterUntilAncestorIsNotChosen,
    sourceFieldValue,
  ]);
  // Disable when the selected ancestor (e.g. shift) has no allowed items (e.g. no meals).
  const isDisabledByEmptyAncestorArray = useMemo(
    () => !!matchAncestorItemArrayKey && allowedValues?.length === 0,
    [matchAncestorItemArrayKey, allowedValues],
  );
  const isDisabled = useMemo(
    () =>
      disabled || isDisabledByMissingAncestor || isDisabledByEmptyAncestorArray,
    [disabled, isDisabledByMissingAncestor, isDisabledByEmptyAncestorArray],
  );
  // In bulk the ancestor cannot be chosen, so an empty intersection is a dead end the user must be told
  // about; elsewhere the hint stays the "pick the ancestor first" one.
  // `ancestorItems` gates the hint so the field stays plainly disabled while they load, instead of
  // flashing "nothing in common" before the ancestors are even known.
  const isDisabledHintShown =
    isDisabledByMissingAncestor ||
    (!!bulkAllowedValues && !!ancestorItems && isDisabledByEmptyAncestorArray);

  const disabledFormLabelTranslated = bulkAllowedValues
    ? t("general.NO_SHARED_OPTIONS_FOR_SELECTED_RECORDS")
    : t("general.PLEASE_SELECT_PREREQUISITE_FIRST", {
        prerequisite: t(
          `${dropItemsSource}.formLabel.${removeMetadataPrefix(ancestorFieldName ?? "")}`,
        ),
      });

  const isAddButtonVisible = useMemo(() => {
    return (
      isAddDropdownButtonVisible &&
      !dropItemsDisableAdd &&
      !isDisabled &&
      isHasCreatePermission
    );
  }, [
    isAddDropdownButtonVisible,
    dropItemsDisableAdd,
    isDisabled,
    isHasCreatePermission,
  ]);

  const loadDropdownItems = useEvent(
    async ({
      sourceEntity,
      forceRefetch = false,
    }: {
      sourceEntity?: RydeUiUnit;
      forceRefetch?: boolean;
    }) => {
      const dropDownItemsParams =
        dropdownItemsSuffixOverride || dropdownItemsResolverName
          ? { dropdownItemsSuffixOverride, dropdownItemsResolverName }
          : undefined;
      const items = await getDropdownItems?.(
        sourceEntity || "unknown",
        forceRefetch,
        dropDownItemsParams,
      );

      setDropdownItems(items || []);
      return items;
    },
  );

  const fetchItems = useCallback(
    async (forceRefetch = false): Promise<IDropdownItem[] | undefined> => {
      let items: IDropdownItem[] | undefined;
      await new Promise<void>(resolve => {
        startTransition(() => {
          (async () => {
            try {
              items = await loadDropdownItems({
                sourceEntity: dropItemsSource,
                forceRefetch,
              });
            } finally {
              resolve();
            }
          })();
        });
      });
      return items;
    },
    [dropItemsSource, loadDropdownItems],
  );

  const handleInsertItem = useCallback(async (): Promise<void> => {
    let insertDialogResult: SimpleResult<IExtendableDto>;

    if (EntitiesWithoutPreCreateForm.includes(entity)) {
      insertDialogResult = await openEditFormDrawer({
        entity: dropItemsSource || "unknown",
        mode: EditFormMode.UnboundDrawer,
        id: undefined,
        canResolve: savedDto => {
          if (!savedDto || !isSimpleDto(savedDto)) {
            return simpleResultFalse;
          }

          return simpleResultTrue;
        },
      });
    } else {
      insertDialogResult = await openNewOrDuplicateDrawer({
        entity: dropItemsSource || "unknown",
        canResolve: savedDto => {
          if (!savedDto || !isSimpleDto(savedDto)) {
            return simpleResultFalse;
          }

          return simpleResultTrue;
        },
      });
    }

    const newId = insertDialogResult.data?.id;

    if (newId) {
      checkCleanupNestedValidation(name, errors);

      await fetchItems(true);

      if (multiMode === "single") {
        Log.debug(
          `Dropdown template for ${name}, updated field value after insert new item:`,
          newId,
        );
        setValue(name, +newId, {
          shouldDirty: true,
          shouldValidate: true,
        });
      } else if (multiMode === "multi" || multiMode === "tags") {
        const curValues = getValues(name) || [];
        const newValueArray = unique([...curValues, +newId]);
        Log.debug(
          `Dropdown ${multiMode} template for ${name}, updated field value after insert new item:`,
          newValueArray,
        );
        setValue(name, newValueArray, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [
    dropItemsSource,
    entity,
    errors,
    fetchItems,
    getValues,
    multiMode,
    name,
    openEditFormDrawer,
    openNewOrDuplicateDrawer,
    setValue,
  ]);

  const handleInsertTagItem = useCallback(
    async (newTagName: string): Promise<SimpleResult<unknown>> => {
      if (!newTagName) {
        return new SimpleResult({
          error: errorsConst.systemTags.invalidTagName,
        });
      }

      setErrors(undefined);
      setLoading(true);

      const updatedDto = { name: newTagName, companyId, id: undefined };
      const createdTagResult = await CrudApi.getOrCreate(
        "systemTag",
        updatedDto,
      );

      if (!createdTagResult) {
        return simpleResultErrorEmptyResponse(t);
      }

      if (!createdTagResult?.success) {
        return createdTagResult as SimpleResult<void>;
      }

      const createdTag = createdTagResult.data;
      const newId = createdTag?.id;

      if (!newId) {
        setLoading(false);
        setErrors([t(errorsConst.systemTags.tagNotSaved)]);
        return new SimpleResult({ error: errorsConst.systemTags.tagNotSaved });
      }

      checkCleanupNestedValidation(name, errors);

      setLoading(true);
      await fetchItems(true);

      const curValues = getValues(name) || [];
      const newValueArray = unique([...curValues, +newId]);
      Log.debug(
        `Dropdown ${multiMode} template for ${name}, updated field value after insert new item:`,
        newValueArray,
      );
      setValue(name, newValueArray, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
      setLoading(false);
      return new SimpleResult({ success: true });
    },
    [
      companyId,
      errors,
      fetchItems,
      getValues,
      multiMode,
      name,
      setErrors,
      setLoading,
      setValue,
      t,
    ],
  );

  useEffect(() => {
    if (dropItemsSource && dropItemsSource !== "unknown") {
      fetchItems();
    }
  }, [
    dropItemsSource,
    fetchItems,
    getDropdownItems,
    dropdownItemsSuffixOverride,
    dropdownItemsResolverName,
    dropdownItemsReloadKey,
  ]);

  useEffect(() => {
    if (dropdownDataTransformationFn && dropdownItems) {
      const transformedDropdownItems = getTransformedDropdownItems({
        controlName: name,
        dropdownDataTransformationFn,
        dropdownItems,
      });

      if (
        Array.isArray(transformedDropdownItems) &&
        !isEqual(transformedDropdownItems, dropdownItems)
      ) {
        setDropdownItems(transformedDropdownItems);
      }
    }
  }, [
    dropdownDataTransformationFn,
    dropdownItems,
    getTransformedDropdownItems,
    name,
  ]);

  useEffect(() => {
    if (
      !isBulkEditForm ||
      !bulkAssignedValuesUnion.length ||
      !dropdownItems?.length ||
      !dropItemsSource ||
      dropItemsSource === "unknown"
    ) {
      return;
    }

    // RYDE-7753: ids stored on rows but absent from the active catalog still need a label
    const existingIds = new Set(dropdownItems.map(item => String(item.value)));
    const missingIds = bulkAssignedValuesUnion.filter(
      id => !existingIds.has(String(id)),
    );

    if (!missingIds.length) {
      return;
    }

    let cancelled = false;

    const loadSyntheticItems = async (): Promise<void> => {
      const numericIds = missingIds
        .map(id => Number(id))
        .filter(id => Number.isFinite(id));
      const labels =
        numericIds.length && getDropdownLabels
          ? await getDropdownLabels(dropItemsSource, numericIds)
          : [];
      const labelsById = new Map(
        numericIds.map((id, index) => [String(id), labels[index]]),
      );

      if (cancelled) {
        return;
      }

      const syntheticItems: IDropdownItem[] = missingIds.map(id => ({
        value: id,
        label: labelsById.get(String(id)) || String(id),
        isActive: false,
      }));

      setDropdownItems(prev => {
        const prevIds = new Set((prev ?? []).map(item => String(item.value)));
        const toAdd = syntheticItems.filter(
          item => !prevIds.has(String(item.value)),
        );

        return toAdd.length ? [...(prev ?? []), ...toAdd] : prev;
      });
    };

    void loadSyntheticItems();

    return () => {
      cancelled = true;
    };
  }, [
    bulkAssignedValuesUnion,
    dropdownItems,
    dropItemsSource,
    getDropdownLabels,
    isBulkEditForm,
  ]);

  const [dropdownOptions, mapOptionLabel] = useMemo(
    () =>
      getDropdownOptions({
        ownFieldName,
        dropdownItems: (!disableSorting
          ? sortArrayAlphabetically<IDropdownItem>(dropdownItems, "label")
          : dropdownItems) as IDropdownItemExt[],
        sourceFieldValue,
        filters,
        customActiveFieldName,
        excludeIds,
        currentFieldValue,
        customDropdownItemsFilter,
        allowedValues,
        bulkAssignedValuesSet,
        transformLabelFn: dropDownTemplate
          ? itemData =>
              dropDownBoundTemplateFormat({
                itemData,
                dropDownTemplate,
              })
          : undefined,
      }),
    [
      ownFieldName,
      disableSorting,
      dropdownItems,
      sourceFieldValue,
      filters,
      customActiveFieldName,
      excludeIds,
      currentFieldValue,
      customDropdownItemsFilter,
      allowedValues,
      bulkAssignedValuesSet,
      dropDownTemplate,
    ],
  );
  const getOptionLabel = useCallback(
    (value: unknown) => {
      return mapOptionLabel.get(value as string | number) || "";
    },
    [mapOptionLabel],
  );
  const customFilter = createFilterOptions();

  const filteredDropdownOptions = useMemo(
    () =>
      customFilter(dropdownOptions ?? [], {
        inputValue: inputText,
        getOptionLabel,
      }) as (string | number | boolean)[],
    [customFilter, dropdownOptions, getOptionLabel, inputText],
  );

  const previousSourceFieldValueRef = useRef<unknown>(undefined);

  // IMPORTANT: this reset effect must stay declared BEFORE the previousSourceFieldValueRef update effect
  // below. React runs effects in declaration order, so this effect reads the *previous* source value
  // (to distinguish a real ancestor change from initial load) before the effect below overwrites the ref
  // with the current value. Reordering them would make previousSourceFieldValueRef.current already equal
  // sourceFieldValue here, breaking the ancestor-change detection.
  useEffect(() => {
    if (multiMode !== "single") {
      return;
    }

    if (
      (ownFieldName || ancestorFieldName) &&
      !isIgnoreFilterUntilAncestorIsNotChosen &&
      (sourceFieldValue === null ||
        // Selected ancestor (e.g. a shift with no meals) leaves no valid options for a set value.
        (isDisabledByEmptyAncestorArray && !isNil(getValues(name))) ||
        (dropdownOptions &&
          !isNil(getValues(name)) &&
          !dropdownOptions.some(e => String(e) === String(getValues(name)))))
    ) {
      const previousValue = getValues(name);
      // Notify the user only when a real ancestor change (e.g. a shift change) clears a value
      // the record already had — never on initial load (ref starts undefined).
      const isAncestorChange =
        previousSourceFieldValueRef.current !== undefined &&
        previousSourceFieldValueRef.current !== sourceFieldValue;

      if (
        clearedByAncestorMessageKey &&
        isAncestorChange &&
        !isNil(previousValue)
      ) {
        Log.infoAndToast(t(clearedByAncestorMessageKey));
      }

      Log.debug(
        `Dropdown force reset to null dropdown for: ${name},` +
          `ancestor field name: ${ancestorFieldName},` +
          `own field name: ${ownFieldName},` +
          `source field value:`,
        sourceFieldValue,
        `value:`,
        getValues(name),
        dropdownOptions,
      );
      setValue(name, null, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [
    getValues,
    multiMode,
    name,
    resetField,
    sourceFieldValue,
    dropdownOptions,
    ancestorFieldName,
    ownFieldName,
    setValue,
    isIgnoreFilterUntilAncestorIsNotChosen,
    isDisabledByEmptyAncestorArray,
    clearedByAncestorMessageKey,
    t,
  ]);

  // Keep this AFTER the reset effect above — it records the current source value for that effect's
  // next-run comparison, and must not overwrite the ref before the reset effect has read it.
  useEffect(() => {
    previousSourceFieldValueRef.current = sourceFieldValue;
  }, [sourceFieldValue]);

  const selectAll = useEvent((): void => {
    const isSelectAll =
      getValues(name)?.length !== filteredDropdownOptions?.length;
    Log.debug("Select all/none dropdown items for field", name, isSelectAll);
    setValue(name, isSelectAll ? filteredDropdownOptions : [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  const showFindAreaButtonEnabled =
    !isBulkEditForm && entity === "station" && name === "areaId";

  const { showFindAreaButton, isFindingArea, handleFindArea } =
    useFindAreaButton({
      name,
      dropItemsSource,
      showFindAreaButtonFromSchema: showFindAreaButtonEnabled,
    });

  return {
    isAddButtonVisible,
    requiredComputed,
    dropdownOptions: filteredDropdownOptions,
    mapOptionLabel,
    getOptionLabel,
    isDisabled,
    isDisabledByMissingAncestor,
    isDisabledHintShown,
    disabledFormLabelTranslated,
    dropdownItems,
    handleInsertItem,
    handleInsertTagItem,
    selectAll,
    fetchItems,
    setInputText,
    inputText,
    isLoadingItems,
    displayTextReplaced,
    exclusiveIds,
    showFindAreaButton,
    isFindingArea,
    handleFindArea,
  };
};
